import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Mic, Calendar, Building2, Clock, CheckCircle2, AlertCircle, ChevronRight, ChevronDown, ChevronLeft, X, Pencil, Trash2, ListTodo, FileText, MoreHorizontal, ArrowLeft, Filter, User, History, Search, BarChart3, MapPin, Users, Briefcase, Phone, Mail, Video, Maximize2, Minimize2, ExternalLink, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import ActivityDialog from '../components/ActivityDialog';
import ClientTypeFilter from '../components/ClientTypeFilter';
import { matchesClientTypeFilter } from '../utils/clientType';
import { ActivitySearchBar } from '../components/activity';
import { ActivitiesAnalytics } from '../components/analytics';
import { Checkbox } from '@/components/ui/checkbox';
import RemindDialog from '../components/RemindDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Client type badges configuration - matching Contacts page colors
const clientTypeBadge = {
  'typsa': { label: 'TYPSA', color: 'bg-red-100 text-red-800 border border-red-300' },
  'privada': { label: 'Privada', color: 'bg-purple-100 text-purple-800 border border-purple-300' },
  'publica': { label: 'Pública', color: 'bg-green-100 text-green-800 border border-green-300' }
};

// TYPSA unit type labels
const typsaUnitTypeLabels = {
  'division': 'División',
  'departamento': 'Departamento',
  'territorio': 'Territorio',
  'grupo': 'Grupo'
};

// Parse AI fields from summary text for display
const parseAIFieldsFromSummary = (summary) => {
  const result = {
    key_points: [],
    decisions: [],
    next_steps: [],
    risks_concerns: [],
    opportunity_signals: []
  };
  
  if (!summary) return result;
  
  const sections = {
    'puntos clave': 'key_points',
    'key points': 'key_points',
    'decisiones': 'decisions',
    'decisions': 'decisions',
    'próximos pasos': 'next_steps',
    'next steps': 'next_steps',
    'riesgos': 'risks_concerns',
    'risks': 'risks_concerns',
    'oportunidades': 'opportunity_signals',
    'opportunities': 'opportunity_signals'
  };
  
  const regex = /\*\*([^*]+):\*\*\s*([\s\S]*?)(?=\*\*[^*]+:\*\*|$)/gi;
  let match;
  
  while ((match = regex.exec(summary)) !== null) {
    const label = match[1].trim().toLowerCase();
    const content = match[2].trim();
    const fieldKey = sections[label];
    
    if (fieldKey && content) {
      const items = content
        .split(/(?:^|\n)\s*[•\-\*]\s*|\n/)
        .map(item => item.trim())
        .filter(item => item.length > 0);
      
      if (items.length > 0) {
        result[fieldKey] = items;
      }
    }
  }
  
  return result;
};

const Activities = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEditor, isAdmin, editorUsers, user } = useUser();
  const [activities, setActivities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Detail panel state
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Reminder-by-email (admin only)
  const [selectedForReminder, setSelectedForReminder] = useState(new Set());
  const [remindDialogOpen, setRemindDialogOpen] = useState(false);
  const [remindItems, setRemindItems] = useState([]);
  
  // Calendar view state for "Hoy" - default to calendar expanded
  const [proximasView, setProximasView] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarExpanded, setCalendarExpanded] = useState(true);
  
  // Filters for each column - default to current user ("Yo")
  const [pendingOwnerFilter, setPendingOwnerFilter] = useState('pending_init');
  const [pasadasOwnerFilter, setPasadasOwnerFilter] = useState('pending_init');
  const [proximasOwnerFilter, setProximasOwnerFilter] = useState('pending_init');
  
  // Initialize owner filter from URL params (for "my activities" from MySpace)
  const ownerFromUrl = searchParams.get('owner');
  const filterByMyActivities = ownerFromUrl === 'me';

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState(filterByMyActivities && user?.name ? user.name : 'all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [activeView, setActiveView] = useState('columns'); // 'columns' or 'search'
  
  // Client type filter state
  const [clientTypeFilter, setClientTypeFilter] = useState(['typsa_gg', 'typsa_colab', 'privada', 'publica']);
  
  // Update owner filter when user data loads and URL param is 'me'
  useEffect(() => {
    if (filterByMyActivities && user?.name && ownerFilter === 'all') {
      setOwnerFilter(user.name);
    }
  }, [filterByMyActivities, user?.name]);

  // Initialize "Yo" filters when user data loads
  useEffect(() => {
    if (user?.name) {
      if (pendingOwnerFilter === 'pending_init') {
        setPendingOwnerFilter(user.name);
      }
      if (pasadasOwnerFilter === 'pending_init') {
        setPasadasOwnerFilter(user.name);
      }
      if (proximasOwnerFilter === 'pending_init') {
        setProximasOwnerFilter(user.name);
      }
    }
  }, [user?.name]);

  useEffect(() => {
    fetchData();
    
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = async (options = {}) => {
    const { silent = false, only = null } = options;
    
    // Don't show loading on silent refreshes
    if (!silent && activities.length === 0) {
      setLoading(true);
    }
    
    try {
      if (only) {
        // Selective fetch
        const endpointMap = {
          'activities': async () => {
            const res = await axios.get(`${API}/activities`);
            setActivities(res.data);
          },
          'tasks': async () => {
            const res = await axios.get(`${API}/tasks`);
            setTasks(res.data);
          }
        };
        const fetchPromises = only.map(key => endpointMap[key]?.()).filter(Boolean);
        await Promise.all(fetchPromises);
        return;
      }
      
      // Full fetch - preserve existing data during load
      const [activitiesRes, companiesRes, tasksRes, typsaUnitsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/tasks`),
        axios.get(`${API}/typsa-org-units`),
        axios.get(`${API}/contacts`)
      ]);
      setActivities(activitiesRes.data);
      setCompanies(companiesRes.data);
      setTasks(tasksRes.data);
      setTypsaOrgUnits(typsaUnitsRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-open activity from URL ?id= param (called from Dashboard)
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl && activities.length > 0) {
      const found = activities.find(a => a.id === idFromUrl);
      if (found) {
        setSelectedActivity(found);
        setDetailOpen(true);
        // Clean up URL param without re-render
        setSearchParams(prev => { prev.delete('id'); return prev; }, { replace: true });
      }
    }
  }, [activities, searchParams]);

  // Helper functions
  const getCompanyName = (companyId) => {
    if (!companyId) return null;
    const company = companies.find(c => c.id === companyId);
    return company?.name || null;
  };

  const getCompanyInfo = (companyId) => {
    if (!companyId) return null;
    return companies.find(c => c.id === companyId);
  };

  const getTypsaOrgUnit = (unitId) => {
    if (!unitId) return null;
    return typsaOrgUnits.find(u => u.id === unitId);
  };

  // Get contact names from contact IDs
  const getContactNames = (contactIds) => {
    if (!contactIds || contactIds.length === 0) return [];
    return contactIds
      .map(cid => {
        const contact = contacts.find(c => c.id === cid);
        if (!contact) return null;
        const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
        return name || contact.email;
      })
      .filter(Boolean);
  };

  // Get unique owners from all activities for filter dropdown
  const uniqueOwners = useMemo(() => {
    const owners = activities
      .map(a => a.owner)
      .filter((owner, index, self) => owner && self.indexOf(owner) === index);
    return owners.sort();
  }, [activities]);

  // Toggle client type filter
  const toggleClientTypeFilter = (type) => {
    setClientTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Determine activity's client type
  const getActivityClientType = (activity) => {
    if (activity.typsa_org_unit_id) {
      return 'typsa';
    }
    const company = companies.find(c => c.id === activity.company_id);
    if (company) {
      if (company.company_type === 'typsa' || company.name?.toUpperCase().includes('TYPSA')) {
        return 'typsa';
      }
      return company.company_type; // 'privada' or 'publica'
    }
    return null;
  };

  const getTypeLabel = (type) => {
    const labels = {
      meeting: 'Reunión',
      call: 'Llamada',
      email: 'Email',
      reflexion: 'Reflexión',
      evento: 'Evento'
    };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      meeting: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300',
      call: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
      email: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300',
      reflexion: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
      evento: 'bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-300'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Determine activity status
  // An activity is documented if it has ANY of: summary, notes, key_points, decisions, next_steps
  const getActivityStatus = (activity) => {
    const hasSummary = activity.summary?.trim();
    const hasNotes = activity.notes?.trim();
    const hasKeyPoints = activity.key_points?.length > 0;
    const hasDecisions = activity.decisions?.length > 0;
    const hasNextSteps = activity.next_steps?.length > 0;
    
    const isDocumented = hasSummary || hasNotes || hasKeyPoints || hasDecisions || hasNextSteps;
    
    // Check if has open tasks or next_steps mentioned
    const activityTasks = tasks.filter(t => t.activity_id === activity.id && t.status !== 'completada');
    const hasOpenTasks = activityTasks.length > 0;
    const hasNextStepsMentioned = activity.summary?.toLowerCase().includes('próximos pasos') || 
                         activity.summary?.toLowerCase().includes('next_steps');
    
    if (!isDocumented) return 'pending';
    if (hasOpenTasks || hasNextStepsMentioned) return 'followup';
    return 'documented';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="destructive" className="text-xs">Sin documentar</Badge>;
      case 'followup':
        return <Badge className="bg-blue-500 text-xs">En seguimiento</Badge>;
      case 'documented':
        return <Badge variant="secondary" className="text-xs">Documentada</Badge>;
      default:
        return null;
    }
  };

  // Categorize activities (with client type filter applied)
  const categorizedActivities = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const pending = [];
    const upcoming = [];
    const past = []; // Renamed from followup
    
    activities.forEach(activity => {
      // Filter by client type first
      const clientType = getActivityClientType(activity);
      if (clientType && !matchesClientTypeFilter(clientType, clientTypeFilter)) {
        return; // Skip activities that don't match the filter
      }
      
      // Filter by owner if coming from MySpace with ?owner=me
      if (filterByMyActivities && user?.name && activity.owner !== user.name) {
        return; // Skip activities not owned by current user
      }
      
      const activityDate = new Date(activity.date);
      activityDate.setHours(0, 0, 0, 0);
      const status = getActivityStatus(activity);
      
      if (status === 'pending' && activityDate < today) {
        // Past activities without documentation -> Pending
        pending.push({ ...activity, status });
      } else if (activityDate >= today) {
        // Future or today activities -> Upcoming (Próximas)
        upcoming.push({ ...activity, status });
      } else {
        // Past documented activities -> Past (Pasadas)
        past.push({ ...activity, status });
      }
    });
    
    // Sort by date
    pending.sort((a, b) => new Date(b.date) - new Date(a.date));
    upcoming.sort((a, b) => new Date(a.date) - new Date(b.date));
    past.sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
    
    return { pending, upcoming, past };
  }, [activities, tasks, clientTypeFilter, companies, typsaOrgUnits, filterByMyActivities, user?.name]);

  // Filter past activities based on selected filters
  const filteredPastActivities = useMemo(() => {
    let filtered = [...categorizedActivities.past];
    
    // Filter by owner (ignore pending_init state)
    if (pasadasOwnerFilter !== 'all' && pasadasOwnerFilter !== 'pending_init') {
      if (pasadasOwnerFilter === 'unassigned') {
        filtered = filtered.filter(activity => !activity.owner || activity.owner.trim() === '');
      } else {
        filtered = filtered.filter(activity => activity.owner === pasadasOwnerFilter);
      }
    }
    
    return filtered;
  }, [categorizedActivities.past, pasadasOwnerFilter]);

  // Filter pending activities based on selected filters
  const filteredPendingActivities = useMemo(() => {
    let filtered = [...categorizedActivities.pending];
    
    // Filter by owner (ignore pending_init state)
    if (pendingOwnerFilter !== 'all' && pendingOwnerFilter !== 'pending_init') {
      if (pendingOwnerFilter === 'unassigned') {
        filtered = filtered.filter(activity => !activity.owner || activity.owner.trim() === '');
      } else {
        filtered = filtered.filter(activity => activity.owner === pendingOwnerFilter);
      }
    }
    
    return filtered;
  }, [categorizedActivities.pending, pendingOwnerFilter]);

  // Filter upcoming activities based on selected filters
  const filteredUpcomingActivities = useMemo(() => {
    let filtered = [...categorizedActivities.upcoming];
    
    // Filter by owner (ignore pending_init state)
    if (proximasOwnerFilter !== 'all' && proximasOwnerFilter !== 'pending_init') {
      if (proximasOwnerFilter === 'unassigned') {
        filtered = filtered.filter(activity => !activity.owner || activity.owner.trim() === '');
      } else {
        filtered = filtered.filter(activity => activity.owner === proximasOwnerFilter);
      }
    }
    
    return filtered;
  }, [categorizedActivities.upcoming, proximasOwnerFilter]);

  // Search and filter all activities (with client type filter)
  const searchedActivities = useMemo(() => {
    let filtered = [...activities];
    
    // Filter by client type
    filtered = filtered.filter(activity => {
      const clientType = getActivityClientType(activity);
      return !clientType || matchesClientTypeFilter(clientType, clientTypeFilter);
    });
    
    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(activity => {
        const searchFields = [
          activity.title,
          activity.summary,
          activity.notes,
          activity.owner
        ].filter(Boolean).join(' ').toLowerCase();
        return searchFields.includes(query);
      });
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.type === typeFilter);
    }
    
    // Company filter
    if (companyFilter !== 'all') {
      if (companyFilter === 'typsa') {
        filtered = filtered.filter(a => {
          const typsaUnit = getTypsaOrgUnit(a.typsa_org_unit_id);
          const company = getCompanyInfo(a.company_id);
          return typsaUnit || company?.name?.toUpperCase().includes('TYPSA');
        });
      } else if (companyFilter.startsWith('typsa:')) {
        const unitId = companyFilter.replace('typsa:', '');
        filtered = filtered.filter(a => a.typsa_org_unit_id === unitId);
      } else if (companyFilter.startsWith('company:')) {
        const companyId = companyFilter.replace('company:', '');
        filtered = filtered.filter(a => a.company_id === companyId);
      }
    }
    
    // Owner filter
    if (ownerFilter !== 'all') {
      if (ownerFilter === 'unassigned') {
        filtered = filtered.filter(a => !a.owner || a.owner.trim() === '');
      } else {
        filtered = filtered.filter(a => a.owner === ownerFilter);
      }
    }
    
    // Date range filter
    if (dateFromFilter) {
      filtered = filtered.filter(a => a.date >= dateFromFilter);
    }
    if (dateToFilter) {
      filtered = filtered.filter(a => a.date <= dateToFilter);
    }
    
    // Sort by date descending
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    return filtered;
  }, [activities, searchQuery, typeFilter, companyFilter, ownerFilter, dateFromFilter, dateToFilter, companies, typsaOrgUnits, clientTypeFilter]);

  // Check if any search/filter is active
  const hasActiveFilters = searchQuery || typeFilter !== 'all' || companyFilter !== 'all' || 
                           ownerFilter !== 'all' || dateFromFilter || dateToFilter;

  // Clear all search/filters
  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setCompanyFilter('all');
    setOwnerFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
  };

  // Highlight search term in text
  const highlightText = (text, query) => {
    if (!text || !query.trim()) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">{part}</mark>
        : part
    );
  };

  // Handlers
  const handleActivityClick = (activity) => {
    setSelectedActivity(activity);
    setDetailOpen(true);
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    setDialogOpen(true);
    setDetailOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/activities/${deleteConfirm.id}`);
      toast.success('Actividad eliminada');
      setDetailOpen(false);
      setSelectedActivity(null);
      fetchData();
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Error al eliminar actividad');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const handleRecordVoiceNote = (activity) => {
    // Navigate to voice note creation with activity context
    navigate(`/voice-notes/create?activity_id=${activity.id}&company_id=${activity.company_id || ''}`);
  };

  const handleDialogSuccess = () => {
    fetchData({ silent: true, only: ['activities'] });
    setDialogOpen(false);
    setEditingActivity(null);
  };

  // Activity Card Component
  // ─── Reminder-by-email helpers (admin only) ───
  const toggleReminderSelection = (id) => {
    setSelectedForReminder(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const resolveOwnerEmail = (ownerName) => {
    if (!ownerName) return '';
    const match = (editorUsers || []).find(u => (u.name || '').trim().toLowerCase() === ownerName.trim().toLowerCase());
    return match?.email || '';
  };

  const buildReminderItemFromActivity = (a) => ({
    type: 'activity',
    id: a.id,
    title: a.title || `${a.type || 'Actividad'} - ${a.date || ''}`,
    owner_name: a.owner || a.created_by_name || 'Sin responsable',
    owner_email: a.created_by_email || resolveOwnerEmail(a.owner) || resolveOwnerEmail(a.created_by_name),
  });

  const openReminderForSingle = (activity) => {
    setRemindItems([buildReminderItemFromActivity(activity)]);
    setRemindDialogOpen(true);
  };

  const openReminderForSelected = () => {
    const byId = new Map(activities.map(a => [a.id, a]));
    const chosen = Array.from(selectedForReminder).map(id => byId.get(id)).filter(Boolean).map(buildReminderItemFromActivity);
    if (chosen.length === 0) {
      toast.error('Selecciona al menos una actividad');
      return;
    }
    setRemindItems(chosen);
    setRemindDialogOpen(true);
  };

  const handleReminderSent = () => {
    setSelectedForReminder(new Set());
  };

  const ActivityCard = ({ activity, showRecordButton = false }) => {
    const company = getCompanyInfo(activity.company_id);
    const companyName = company?.name;
    const clientType = getActivityClientType(activity);
    const typsaUnit = getTypsaOrgUnit(activity.typsa_org_unit_id);
    const contactNames = getContactNames(activity.contact_ids);
    const status = getActivityStatus(activity);
    const isPending = status === 'pending';
    const isSelected = selectedForReminder.has(activity.id);
    
    return (
      <Card 
        className={`cursor-pointer hover:shadow-md transition-shadow group ${isSelected ? 'ring-2 ring-primary/60' : ''}`}
        onClick={() => handleActivityClick(activity)}
        data-testid={`activity-card-${activity.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            {isAdmin && isPending && (
              <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => toggleReminderSelection(activity.id)}
                  aria-label="Seleccionar para recordar por correo"
                  data-testid={`activity-remind-checkbox-${activity.id}`}
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {/* Badges row */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Badge className={`${getTypeBadgeColor(activity.type)} text-xs shrink-0`}>
                  {getTypeLabel(activity.type)}
                </Badge>
                {getStatusBadge(status)}
                {/* Client type badge */}
                {clientType && clientTypeBadge[clientType] && (
                  <Badge className={`${clientTypeBadge[clientType].color} text-xs shrink-0`}>
                    {clientTypeBadge[clientType].label}
                  </Badge>
                )}
              </div>
              
              {/* Title */}
              <h3 className="font-medium text-sm line-clamp-1 mb-2">
                {activity.title || `${getTypeLabel(activity.type)} - ${activity.date}`}
              </h3>
              
              {/* Info rows - Desktop */}
              <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {/* Date and time */}
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {activity.date}
                  {activity.time && <span>• {activity.time}</span>}
                </span>
                
                {/* Company name */}
                {companyName && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    <span className="font-medium">{companyName}</span>
                  </span>
                )}
                
                {/* TYPSA Unit */}
                {typsaUnit && (
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    <span className="font-medium">{typsaUnit.value}</span>
                    <span className="text-muted-foreground">
                      ({typsaUnitTypeLabels[typsaUnit.category] || typsaUnit.category})
                    </span>
                  </span>
                )}
                
                {/* Owner */}
                {activity.owner && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {activity.owner}
                  </span>
                )}
                
                {/* Location */}
                {activity.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {activity.location}
                  </span>
                )}
                
                {/* Contacts */}
                {contactNames.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {contactNames.length <= 2 
                      ? contactNames.join(', ')
                      : `${contactNames[0]}, ${contactNames[1]} +${contactNames.length - 2}`}
                  </span>
                )}
                
                {/* Content Link */}
                {activity.content_link && (
                  <a
                    href={activity.content_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>Ver contenido</span>
                  </a>
                )}
              </div>
              
              {/* Info rows - Mobile (compact) */}
              <div className="sm:hidden space-y-1 text-xs text-muted-foreground">
                {/* Date */}
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3 shrink-0" />
                  <span>{activity.date}</span>
                  {activity.time && <span>• {activity.time}</span>}
                </div>
                
                {/* Company or TYPSA Unit */}
                {(companyName || typsaUnit) && (
                  <div className="flex items-center gap-2">
                    {typsaUnit ? (
                      <>
                        <Briefcase className="w-3 h-3 shrink-0" />
                        <span className="truncate">{typsaUnit.value}</span>
                        <span className="shrink-0">({typsaUnitTypeLabels[typsaUnit.category] || typsaUnit.category})</span>
                      </>
                    ) : (
                      <>
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{companyName}</span>
                      </>
                    )}
                  </div>
                )}
                
                {/* Owner and contacts on mobile */}
                <div className="flex items-center gap-3">
                  {activity.owner && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <span className="truncate max-w-[100px]">{activity.owner.split(' ')[0]}</span>
                    </span>
                  )}
                  {contactNames.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      <span>{contactNames.length}</span>
                    </span>
                  )}
                  {/* Content Link - Mobile */}
                  {activity.content_link && (
                    <a
                      href={activity.content_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
            
            {showRecordButton && status === 'pending' && isEditor && (
              <Button
                size="sm"
                className="shrink-0 bg-red-500 hover:bg-red-600 hidden sm:flex"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecordVoiceNote(activity);
                }}
                data-testid={`record-btn-${activity.id}`}
              >
                <Mic className="w-4 h-4 mr-1" />
                Registrar
              </Button>
            )}
            {isAdmin && isPending && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 hidden sm:flex"
                onClick={(e) => { e.stopPropagation(); openReminderForSingle(activity); }}
                data-testid={`activity-remind-single-${activity.id}`}
                title="Recordar por correo"
              >
                <Send className="w-4 h-4" />
              </Button>
            )}
            
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 group-hover:translate-x-1 transition-transform" />
          </div>
          
          {/* Mobile record button - full width */}
          {showRecordButton && status === 'pending' && isEditor && (
            <div className="sm:hidden mt-3 pt-3 border-t">
              <Button
                size="sm"
                className="w-full bg-red-500 hover:bg-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRecordVoiceNote(activity);
                }}
              >
                <Mic className="w-4 h-4 mr-2" />
                Registrar actividad
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Activity Detail Panel/Page
  const ActivityDetail = ({ activity, onClose }) => {
    if (!activity) return null;
    
    const companyName = getCompanyName(activity.company_id);
    const activityTasks = tasks.filter(t => t.activity_id === activity.id);
    const status = getActivityStatus(activity);
    const isDocumented = status !== 'pending';
    
    const content = (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Badge className={`${getTypeBadgeColor(activity.type)} mb-2`}>
              {getTypeLabel(activity.type)}
            </Badge>
            <h2 className="text-xl font-semibold">
              {activity.title || `${getTypeLabel(activity.type)} - ${activity.date}`}
            </h2>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {activity.date}
              </span>
              {companyName && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {companyName}
                </span>
              )}
              {activity.owner && (
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {activity.owner}
                </span>
              )}
              {/* Content Link in Detail View */}
              {activity.content_link && (
                <a
                  href={activity.content_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                  data-testid="activity-detail-content-link"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Ver contenido enlazado</span>
                </a>
              )}
            </div>
          </div>
          
          {isEditor && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleEdit(activity)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Editar
                </DropdownMenuItem>
                {/* Solo mostrar "Añadir nota de voz" si NO está documentada */}
                {!isDocumented && (
                  <DropdownMenuItem onClick={() => handleRecordVoiceNote(activity)}>
                    <Mic className="w-4 h-4 mr-2" />
                    Documentar con voz
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={() => setDeleteConfirm(activity)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        
        {/* Action buttons for pending - only if not documented */}
        {!isDocumented && isEditor && (
          <div className="flex gap-2">
            <Button 
              className="flex-1 bg-red-500 hover:bg-red-600"
              onClick={() => handleRecordVoiceNote(activity)}
            >
              <Mic className="w-4 h-4 mr-2" />
              Documentar con voz
            </Button>
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => handleEdit(activity)}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Documentar manualmente
            </Button>
          </div>
        )}
        
        {/* Accordion sections */}
        <Accordion type="multiple" defaultValue={['result', 'tasks']} className="w-full">
          {/* Result/Documentation */}
          <AccordionItem value="result">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Resultado de la actividad
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              {(() => {
                // Use dedicated fields if available, otherwise parse from summary
                const aiFields = activity.key_points?.length 
                  ? activity 
                  : parseAIFieldsFromSummary(activity.summary);
                
                const hasAIContent = aiFields.key_points?.length || 
                                     aiFields.decisions?.length || 
                                     aiFields.next_steps?.length ||
                                     aiFields.risks_concerns?.length ||
                                     aiFields.opportunity_signals?.length;
                
                return (
                  <>
                    {/* AI-extracted fields */}
                    {aiFields.key_points?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Puntos Clave</h4>
                        <ul className="text-sm space-y-1">
                          {aiFields.key_points.map((point, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-primary mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {aiFields.decisions?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Decisiones</h4>
                        <ul className="text-sm space-y-1">
                          {aiFields.decisions.map((decision, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-green-600 mt-1">✓</span>
                              <span>{decision}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {aiFields.next_steps?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Próximos Pasos</h4>
                        <ul className="text-sm space-y-1">
                          {aiFields.next_steps.map((step, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-blue-600 mt-1">→</span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {aiFields.risks_concerns?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Riesgos</h4>
                        <ul className="text-sm space-y-1">
                          {aiFields.risks_concerns.map((risk, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-amber-600 mt-1">⚠</span>
                              <span>{risk}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {aiFields.opportunity_signals?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Oportunidades</h4>
                        <ul className="text-sm space-y-1">
                          {aiFields.opportunity_signals.map((opp, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-emerald-600 mt-1">★</span>
                              <span>{opp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Show raw summary only if no structured content was parsed */}
                    {activity.summary && !hasAIContent && (
                      <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                        {activity.summary}
                      </div>
                    )}
                    
                    {/* Notes/Transcript */}
                    {(activity.notes || activity.transcript) && (
                      <div className="pt-4 border-t">
                        <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase">Transcripción/Notas</h4>
                        <p className="text-sm whitespace-pre-wrap">{activity.transcript || activity.notes}</p>
                      </div>
                    )}
                    
                    {/* Empty state */}
                    {!activity.summary && !hasAIContent && !activity.notes && !activity.transcript && (
                      <p className="text-sm text-muted-foreground italic">
                        Sin documentación. Usa el botón "Registrar" para añadir contenido.
                      </p>
                    )}
                  </>
                );
              })()}
            </AccordionContent>
          </AccordionItem>
          
          {/* Tasks */}
          <AccordionItem value="tasks">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <ListTodo className="w-4 h-4" />
                Tareas ({activityTasks.length})
              </div>
            </AccordionTrigger>
            <AccordionContent>
              {activityTasks.length > 0 ? (
                <div className="space-y-2">
                  {activityTasks.map(task => (
                    <div 
                      key={task.id}
                      className="flex items-center gap-2 p-2 rounded-lg bg-muted/50"
                    >
                      <CheckCircle2 className={`w-4 h-4 ${task.status === 'completada' ? 'text-green-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm flex-1 ${task.status === 'completada' ? 'line-through text-muted-foreground' : ''}`}>
                        {task.title}
                      </span>
                      {task.due_date && (
                        <span className="text-xs text-muted-foreground">{task.due_date}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No hay tareas asociadas a esta actividad.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
    
    // Mobile: Full screen
    if (isMobile) {
      return (
        <div className="fixed inset-0 z-50 bg-background">
          <div className="flex items-center justify-between p-4 border-b">
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            {getStatusBadge(getActivityStatus(activity))}
          </div>
          <div className="p-4 overflow-y-auto h-[calc(100vh-60px)]">
            {content}
          </div>
        </div>
      );
    }
    
    // Desktop: Sheet/Drawer
    return (
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="sr-only">Detalle de actividad</SheetTitle>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  };

  // Calendar view for upcoming activities - shows ALL activities for context
  const CalendarView = ({ upcomingActivities, isMobile = false }) => {
    // Get ALL activities for the calendar view (past + future for context)
    const allActivitiesForCalendar = useMemo(() => {
      return activities.map(a => ({
        ...a,
        status: getActivityStatus(a)
      }));
    }, [activities]);

    const getDaysInMonth = (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();
      const startingDay = firstDay.getDay();
      return { daysInMonth, startingDay, year, month };
    };

    const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const dayNames = isMobile ? ['D', 'L', 'M', 'X', 'J', 'V', 'S'] : ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    const todayStr = new Date().toISOString().split('T')[0];

    // Mobile: cell height is smaller
    const cellHeight = isMobile ? 'h-14' : 'h-24';

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className={`${cellHeight} border-b border-r bg-muted/20`} />);
    }

    // Add cells for each day
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayActivities = allActivitiesForCalendar.filter(a => a.date === dateStr);
      const isToday = todayStr === dateStr;
      const isPast = dateStr < todayStr;

      days.push(
        <div 
          key={day} 
          className={`${cellHeight} border-b border-r p-0.5 sm:p-1 relative transition-colors ${
            isToday 
              ? 'bg-blue-100 dark:bg-blue-900/40 ring-2 ring-blue-500 ring-inset z-10' 
              : isPast 
                ? 'bg-muted/30' 
                : 'bg-background hover:bg-muted/50'
          }`}
          onClick={() => {
            // On mobile, clicking a day with activities shows them
            if (isMobile && dayActivities.length > 0) {
              handleActivityClick(dayActivities[0]);
            }
          }}
        >
          {/* Day number */}
          <div className={`text-xs font-medium flex items-center justify-center sm:justify-start gap-0.5 ${
            isToday 
              ? 'text-blue-700 dark:text-blue-300 font-bold' 
              : isPast 
                ? 'text-muted-foreground' 
                : ''
          }`}>
            {isToday ? (
              <span className={`${isMobile ? 'w-5 h-5 text-[9px]' : 'w-5 h-5 text-[10px]'} rounded-full bg-blue-600 text-white flex items-center justify-center`}>
                {day}
              </span>
            ) : (
              <span className={isMobile ? 'text-[10px]' : ''}>{day}</span>
            )}
          </div>
          
          {/* Activities - Desktop shows text, Mobile shows dots */}
          {isMobile ? (
            <div className="flex flex-wrap gap-0.5 justify-center mt-0.5">
              {dayActivities.slice(0, 3).map(activity => (
                <div 
                  key={activity.id}
                  className={`w-1.5 h-1.5 rounded-full ${getTypeBadgeColor(activity.type).replace('text-white', '').replace('text-gray-800', '')} ${isPast ? 'opacity-50' : ''}`}
                  title={activity.title || getTypeLabel(activity.type)}
                />
              ))}
              {dayActivities.length > 3 && (
                <span className="text-[8px] text-muted-foreground">+{dayActivities.length - 3}</span>
              )}
            </div>
          ) : (
            <div className="space-y-0.5 overflow-hidden">
              {dayActivities.slice(0, 2).map(activity => (
                <div 
                  key={activity.id}
                  className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer transition-opacity ${getTypeBadgeColor(activity.type)} ${
                    isPast ? 'opacity-60' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActivityClick(activity);
                  }}
                  title={activity.title || getTypeLabel(activity.type)}
                >
                  {activity.title?.substring(0, 12) || getTypeLabel(activity.type)}
                </div>
              ))}
              {dayActivities.length > 2 && (
                <div className={`text-xs px-1 ${isPast ? 'text-muted-foreground/60' : 'text-muted-foreground'}`}>
                  +{dayActivities.length - 2} más
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-semibold ${isMobile ? 'text-sm' : ''}`}>
            {monthNames[month]} {year}
          </h3>
          <div className="flex gap-1">
            <Button 
              variant="outline" 
              size={isMobile ? 'icon' : 'sm'}
              className={isMobile ? 'h-8 w-8' : ''}
              onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
            >
              ←
            </Button>
            <Button 
              variant="default" 
              size={isMobile ? 'icon' : 'sm'}
              onClick={() => setCurrentMonth(new Date())}
              className={`bg-blue-600 hover:bg-blue-700 ${isMobile ? 'h-8 w-8' : ''}`}
            >
              {isMobile ? '•' : 'Hoy'}
            </Button>
            <Button 
              variant="outline" 
              size={isMobile ? 'icon' : 'sm'}
              className={isMobile ? 'h-8 w-8' : ''}
              onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
            >
              →
            </Button>
          </div>
        </div>
        
        {/* Legend - hidden on mobile */}
        {!isMobile && (
          <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-blue-100 dark:bg-blue-900/40 ring-1 ring-blue-500" />
              Hoy
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-muted/30" />
              Pasado
            </span>
          </div>
        )}
        
        <div className="grid grid-cols-7 border-l border-t rounded-lg overflow-hidden">
          {dayNames.map(day => (
            <div key={day} className={`text-center font-medium border-b border-r bg-muted/50 ${isMobile ? 'text-[10px] py-1' : 'text-xs py-2'}`}>
              {day}
            </div>
          ))}
          {days}
        </div>
        
        {/* Mobile: Activity type legend */}
        {isMobile && (
          <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              Reunión
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              Llamada
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-orange-500" />
              Visita
            </span>
          </div>
        )}
      </div>
    );
  };

  // Section Component - Updated for full height columns
  const Section = ({ title, icon: Icon, count, color, children, defaultOpen = true, className = '', headerExtra = null }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
      <div className={`flex flex-col ${className}`}>
        <div className="flex items-center gap-2 w-full mb-3 shrink-0">
          <button 
            className="flex items-center gap-2 flex-1 text-left"
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className={`w-2 h-2 rounded-full ${color}`} />
            <Icon className="w-4 h-4" />
            <h2 className="font-semibold flex-1">{title}</h2>
            <Badge variant="secondary" className="text-xs">{count}</Badge>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
          </button>
          {headerExtra}
        </div>
        {isOpen && (
          <div className="flex-1 overflow-hidden">
            {children}
          </div>
        )}
      </div>
    );
  };

  // Search Result Card Component
  const SearchResultCard = ({ 
    activity, 
    searchQuery, 
    highlightText, 
    onClick, 
    onEdit, 
    onDelete,
    getCompanyName,
    getTypsaOrgUnit,
    getTypeLabel,
    getTypeBadgeColor,
    isEditor
  }) => {
    const companyName = getCompanyName(activity.company_id);
    const typsaUnit = getTypsaOrgUnit(activity.typsa_org_unit_id);
    
    return (
      <Card 
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={onClick}
        data-testid={`search-result-${activity.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`text-xs ${getTypeBadgeColor(activity.type)}`}>
                  {getTypeLabel(activity.type)}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {new Date(activity.date).toLocaleDateString('es-ES', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </span>
              </div>
              
              <h3 className="font-medium mb-1">
                {highlightText(activity.title, searchQuery)}
              </h3>
              
              {activity.summary && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {highlightText(activity.summary, searchQuery)}
                </p>
              )}
              
              <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-muted-foreground">
                {(companyName || typsaUnit) && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {typsaUnit ? `TYPSA: ${typsaUnit.value || typsaUnit.name}` : companyName}
                  </span>
                )}
                {activity.owner && (
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {activity.owner}
                  </span>
                )}
                {/* Content Link in Search Results */}
                {activity.content_link && (
                  <a
                    href={activity.content_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>Ver contenido</span>
                  </a>
                )}
              </div>
            </div>
            
            {isEditor && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Function to clear "my activities" filter
  const clearMyActivitiesFilter = () => {
    setSearchParams({});
    setOwnerFilter('all');
  };

  return (
    <div className="flex flex-col h-[calc(100vh-120px)] px-4 md:px-8 lg:px-10 overflow-x-hidden" data-testid="activities-page">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold">Actividades</h1>
          <p className="text-sm text-muted-foreground">
            {filterByMyActivities
              ? `Mis actividades sin documentar (${categorizedActivities.pending.length})`
              : hasActiveFilters 
                ? `${searchedActivities.length} resultado${searchedActivities.length !== 1 ? 's' : ''}`
                : `Centro operativo${clientTypeFilter.length < 4 ? ' (filtrado)' : ''}`}
          </p>
        </div>
        
        {/* "My activities" filter indicator */}
        {filterByMyActivities && (
          <div className="flex items-center gap-2 text-sm">
            <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
              <User className="w-3 h-3 mr-1" />
              Solo mis actividades
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-xs"
              onClick={clearMyActivitiesFilter}
              data-testid="clear-my-activities-filter"
            >
              <X className="w-3 h-3 mr-1" />
              Ver todas
            </Button>
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <ClientTypeFilter 
            selectedTypes={clientTypeFilter}
            onToggle={toggleClientTypeFilter}
          />
          <div className="flex items-center gap-2">
            <Button 
              variant={activeView === 'analytics' ? "default" : "outline"}
              onClick={() => setActiveView(activeView === 'analytics' ? 'columns' : 'analytics')}
              data-testid="activities-analytics-btn"
              size="sm"
              className="shrink-0"
            >
              <BarChart3 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Analíticas</span>
            </Button>
            <Button 
              variant={hasActiveFilters ? "default" : "outline"}
              onClick={() => setActiveView(activeView === 'search' ? 'columns' : 'search')}
              data-testid="toggle-search-btn"
              size="sm"
              className="shrink-0"
            >
              <Search className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{hasActiveFilters ? `Buscar (${searchedActivities.length})` : 'Buscar'}</span>
            </Button>
            {isAdmin && selectedForReminder.size > 0 && (
              <Button
                onClick={openReminderForSelected}
                size="sm"
                className="shrink-0"
                data-testid="activities-remind-selected-btn"
              >
                <Send className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Recordar ({selectedForReminder.size})</span>
                <span className="sm:hidden">{selectedForReminder.size}</span>
              </Button>
            )}
            {isEditor && (
              <Button onClick={() => setDialogOpen(true)} data-testid="new-activity-btn" size="sm" className="shrink-0">
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Nueva Actividad</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search Bar - Always visible when search mode is active or has filters */}
      {(activeView === 'search' || hasActiveFilters) && (
        <div className="shrink-0 mb-6">
          <ActivitySearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            ownerFilter={ownerFilter}
            onOwnerFilterChange={setOwnerFilter}
            dateFromFilter={dateFromFilter}
            onDateFromFilterChange={setDateFromFilter}
            dateToFilter={dateToFilter}
            onDateToFilterChange={setDateToFilter}
            owners={uniqueOwners}
            resultCount={searchedActivities.length}
            onClear={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />
        </div>
      )}

      {/* Analytics View */}
      {activeView === 'analytics' ? (
        <ActivitiesAnalytics clientTypeFilter={clientTypeFilter} />
      ) : (
        <>
          {/* Search Results View */}
          {hasActiveFilters ? (
        <div className="flex-1 overflow-y-auto">
          {searchedActivities.length === 0 ? (
            <Card className="bg-muted/50">
              <CardContent className="p-8 text-center">
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">No se encontraron resultados</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Intenta con otros términos de búsqueda o ajusta los filtros
                </p>
                <Button variant="outline" onClick={clearFilters}>
                  Limpiar filtros
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {searchedActivities.map(activity => (
                <SearchResultCard 
                  key={activity.id} 
                  activity={activity} 
                  searchQuery={searchQuery}
                  highlightText={highlightText}
                  onClick={() => handleActivityClick(activity)}
                  onEdit={() => handleEdit(activity)}
                  onDelete={() => setDeleteConfirm(activity)}
                  getCompanyName={getCompanyName}
                  getTypsaOrgUnit={getTypsaOrgUnit}
                  getTypeLabel={getTypeLabel}
                  getTypeBadgeColor={getTypeBadgeColor}
                  isEditor={isEditor}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Main Content - Full Height Layout (Original Columns View) */
        <div className={`flex-1 min-h-0 ${isMobile ? 'space-y-6 overflow-y-auto pb-4' : calendarExpanded ? 'flex gap-2' : 'grid grid-cols-1 lg:grid-cols-3 gap-8'}`}>
        
        {/* Pending (Priority) - Collapsed to sidebar when calendar expanded */}
        {calendarExpanded && !isMobile ? (
          <div 
            className="w-12 shrink-0 flex flex-col items-center py-3 bg-muted/30 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setCalendarExpanded(false)}
            title="Clic para expandir"
            data-testid="collapsed-pending-column"
          >
            <div className="w-3 h-3 rounded-full bg-red-500 mb-2" />
            <AlertCircle className="w-4 h-4 mb-2 text-muted-foreground" />
            <span className="writing-mode-vertical text-xs font-medium text-muted-foreground mb-2" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              Pendientes
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {filteredPendingActivities.length}
            </Badge>
          </div>
        ) : (
        <div className={`${isMobile ? '' : 'lg:col-span-1 flex flex-col min-h-0'}`}>
          <Section 
            title="Pendiente de documentar" 
            icon={AlertCircle} 
            count={filteredPendingActivities.length}
            color="bg-red-500"
            className="h-full"
            defaultOpen={true}
          >
            <div className="h-full flex flex-col">
              {/* Filters */}
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <Select value={pendingOwnerFilter} onValueChange={setPendingOwnerFilter} className="flex-1">
                  <SelectTrigger className="h-8 text-xs" data-testid="pending-owner-filter">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <SelectValue placeholder="Responsable" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                    {uniqueOwners.map(owner => (
                      <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={pendingOwnerFilter === user?.name ? "default" : "outline"}
                  size="sm"
                  className="h-8 px-3 text-xs shrink-0"
                  onClick={() => setPendingOwnerFilter(pendingOwnerFilter === user?.name ? 'all' : user?.name || 'all')}
                  data-testid="pending-filter-me"
                >
                  Yo
                </Button>
              </div>
              
              {/* Filtered count indicator */}
              {pendingOwnerFilter !== 'all' && pendingOwnerFilter !== 'pending_init' && (
                <div className="text-xs text-muted-foreground mb-2 shrink-0">
                  Mostrando {filteredPendingActivities.length} de {categorizedActivities.pending.length}
                </div>
              )}
              
              {/* Scrollable list */}
              <div className="flex-1 overflow-y-auto pr-1">
                {filteredPendingActivities.length === 0 ? (
                  <Card className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                    <CardContent className="p-4 text-center">
                      <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-sm text-green-700 dark:text-green-300">
                        {pendingOwnerFilter !== 'all' && pendingOwnerFilter !== 'pending_init' ? '¡Sin pendientes!' : '¡Todo documentado!'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {filteredPendingActivities.map(activity => (
                      <ActivityCard 
                        key={activity.id} 
                        activity={activity} 
                        showRecordButton={true}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>
        </div>
        )}

        {/* Hoy/Calendar - Expandable */}
        <div className={`${isMobile ? '' : calendarExpanded ? 'flex-1 flex flex-col min-h-0' : 'lg:col-span-1 flex flex-col min-h-0'}`}>
          <Section 
            title="Hoy" 
            icon={Calendar} 
            count={filteredUpcomingActivities.length}
            color="bg-amber-500"
            className="h-full"
            headerExtra={
              !isMobile && (
                <Button
                  variant={calendarExpanded ? "default" : "ghost"}
                  size="sm"
                  className={`h-7 px-2 ml-2 ${calendarExpanded ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
                  onClick={() => setCalendarExpanded(!calendarExpanded)}
                  title={calendarExpanded ? "Contraer calendario" : "Expandir calendario"}
                  data-testid="expand-calendar-btn"
                >
                  {calendarExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  {calendarExpanded && <span className="ml-1 text-xs">Contraer</span>}
                </Button>
              )
            }
          >
            <div className="h-full flex flex-col">
              {/* Filters */}
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <Select value={proximasOwnerFilter} onValueChange={setProximasOwnerFilter} className="flex-1">
                  <SelectTrigger className="h-8 text-xs" data-testid="proximas-owner-filter">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <SelectValue placeholder="Responsable" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                    {uniqueOwners.map(owner => (
                      <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={proximasOwnerFilter === user?.name ? "default" : "outline"}
                  size="sm"
                  className="h-8 px-3 text-xs shrink-0"
                  onClick={() => setProximasOwnerFilter(proximasOwnerFilter === user?.name ? 'all' : user?.name || 'all')}
                  data-testid="proximas-filter-me"
                >
                  Yo
                </Button>
              </div>
              
              {/* Filtered count indicator */}
              {proximasOwnerFilter !== 'all' && proximasOwnerFilter !== 'pending_init' && (
                <div className="text-xs text-muted-foreground mb-2 shrink-0">
                  Mostrando {filteredUpcomingActivities.length} de {categorizedActivities.upcoming.length}
                </div>
              )}
              
              {/* View tabs */}
              <Tabs value={proximasView} onValueChange={setProximasView} className="mb-3 shrink-0">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="calendar">Calendario</TabsTrigger>
                  <TabsTrigger value="list">Lista</TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Content */}
              <div className="flex-1 overflow-y-auto pr-1">
                {proximasView === 'calendar' ? (
                  <CalendarView upcomingActivities={filteredUpcomingActivities} isMobile={isMobile} />
                ) : (
                  filteredUpcomingActivities.length === 0 ? (
                    <Card className="bg-muted/50">
                      <CardContent className="p-4 text-center">
                        <Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {proximasOwnerFilter !== 'all' && proximasOwnerFilter !== 'pending_init' ? 'Sin actividades programadas' : 'No hay actividades programadas'}
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {filteredUpcomingActivities.slice(0, isMobile ? 5 : 10).map(activity => (
                        <ActivityCard key={activity.id} activity={activity} />
                      ))}
                      {filteredUpcomingActivities.length > (isMobile ? 5 : 10) && (
                        <Button variant="ghost" className="w-full" onClick={() => setProximasView('list')}>
                          Ver todas ({filteredUpcomingActivities.length})
                        </Button>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </Section>
        </div>

        {/* Past Activities - Collapsed to sidebar when calendar expanded */}
        {calendarExpanded && !isMobile ? (
          <div 
            className="w-12 shrink-0 flex flex-col items-center py-3 bg-muted/30 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setCalendarExpanded(false)}
            title="Clic para expandir"
            data-testid="collapsed-past-column"
          >
            <div className="w-3 h-3 rounded-full bg-slate-500 mb-2" />
            <History className="w-4 h-4 mb-2 text-muted-foreground" />
            <span className="writing-mode-vertical text-xs font-medium text-muted-foreground mb-2" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              Pasadas
            </span>
            <Badge variant="secondary" className="text-[10px] px-1.5">
              {filteredPastActivities.length}
            </Badge>
          </div>
        ) : (
        <div className={`${isMobile ? '' : 'lg:col-span-1 flex flex-col min-h-0'}`}>
          <Section 
            title="Pasadas" 
            icon={History} 
            count={filteredPastActivities.length}
            color="bg-slate-500"
            defaultOpen={!isMobile}
            className="h-full"
          >
            <div className="h-full flex flex-col">
              {/* Filters for Past Activities */}
              <div className="flex items-center gap-2 mb-3 shrink-0">
                <Select value={pasadasOwnerFilter} onValueChange={setPasadasOwnerFilter} className="flex-1">
                  <SelectTrigger className="h-8 text-xs" data-testid="pasadas-owner-filter">
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      <SelectValue placeholder="Responsable" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                    {uniqueOwners.map(owner => (
                      <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={pasadasOwnerFilter === user?.name ? "default" : "outline"}
                  size="sm"
                  className="h-8 px-3 text-xs shrink-0"
                  onClick={() => setPasadasOwnerFilter(pasadasOwnerFilter === user?.name ? 'all' : user?.name || 'all')}
                  data-testid="pasadas-filter-me"
                >
                  Yo
                </Button>
              </div>
              
              {/* Filtered count indicator */}
              {pasadasOwnerFilter !== 'all' && pasadasOwnerFilter !== 'pending_init' && (
                <div className="text-xs text-muted-foreground mb-2 shrink-0">
                  Mostrando {filteredPastActivities.length} de {categorizedActivities.past.length}
                </div>
              )}
              
              {/* Scrollable list */}
              <div className="flex-1 overflow-y-auto pr-1">
                {filteredPastActivities.length === 0 ? (
                  <Card className="bg-muted/50">
                    <CardContent className="p-4 text-center">
                      <History className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        {categorizedActivities.past.length === 0 
                          ? 'Sin actividades pasadas' 
                          : 'No hay actividades con los filtros seleccionados'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {filteredPastActivities.map(activity => (
                      <ActivityCard key={activity.id} activity={activity} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Section>
        </div>
        )}
      </div>
      )}
      </>
      )}

      {/* Activity Detail Panel */}
      {selectedActivity && (
        <ActivityDetail 
          activity={selectedActivity} 
          onClose={() => {
            setDetailOpen(false);
            setSelectedActivity(null);
          }}
        />
      )}

      {/* Activity Dialog (Create/Edit) */}
      <ActivityDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingActivity(null);
          }
        }}
        onSuccess={handleDialogSuccess}
        activity={editingActivity}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar actividad?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la actividad.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-500 hover:bg-red-600">
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reminder-by-email dialog */}
      <RemindDialog
        open={remindDialogOpen}
        onOpenChange={setRemindDialogOpen}
        items={remindItems}
        onSent={handleReminderSent}
      />
    </div>
  );
};

export default Activities;
